/**
 * Device-bound ECDH keypair management for E2EE.
 *
 * Each (browser, user) combination has exactly one ECDH P-256 keypair stored
 * in IndexedDB. The keypair is non-extractable at the CryptoKey level but we
 * also stash JWK exports for cross-session reload (Web Crypto's `idb` storage
 * model doesn't preserve CryptoKey objects directly across reloads in all
 * browsers, so JWK round-trip is the portable path).
 *
 * `deviceId` is a UUID generated once per (browser, user) and remains stable.
 */

import { getDB } from "./indexedDb";
import { bytesToBase64 } from "./encryption";

export const ECDH_ALGO: EcKeyGenParams = { name: "ECDH", namedCurve: "P-256" };

export interface DeviceKeypair {
  userId: string;
  deviceId: string;
  privateKey: CryptoKey;
  publicKey: CryptoKey;
  publicKeySpkiBase64: string;
  label: string | null;
}

const generateUuid = (): string => {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  // Fallback (Safari < 15.4 etc.)
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const guessDeviceLabel = (): string | null => {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  // Very simple heuristics — good enough for "Twoje urządzenia" UI later.
  let browser = "Browser";
  if (/Edg\//.test(ua)) browser = "Edge";
  else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) browser = "Chrome";
  else if (/Firefox\//.test(ua)) browser = "Firefox";
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = "Safari";
  let os = "";
  if (/Windows/.test(ua)) os = "Windows";
  else if (/Mac OS X/.test(ua)) os = "macOS";
  else if (/Android/.test(ua)) os = "Android";
  else if (/iPhone|iPad/.test(ua)) os = "iOS";
  else if (/Linux/.test(ua)) os = "Linux";
  return os ? `${browser} on ${os}` : browser;
};

const importKeypair = async (
  privateKeyJwk: JsonWebKey,
  publicKeyJwk: JsonWebKey
): Promise<{ privateKey: CryptoKey; publicKey: CryptoKey }> => {
  const privateKey = await crypto.subtle.importKey(
    "jwk",
    privateKeyJwk,
    ECDH_ALGO,
    false,
    ["deriveKey", "deriveBits"]
  );
  const publicKey = await crypto.subtle.importKey(
    "jwk",
    publicKeyJwk,
    ECDH_ALGO,
    true,
    []
  );
  return { privateKey, publicKey };
};

/**
 * Load existing keypair for the user from IndexedDB, or generate a new one.
 * Returns the keypair + public key as base64 SPKI for upload to Convex.
 */
export const getOrCreateDeviceKeypair = async (
  userId: string
): Promise<DeviceKeypair> => {
  const db = await getDB();
  const existing = await db.get("device_keypair", userId);

  if (existing) {
    const { privateKey, publicKey } = await importKeypair(
      existing.privateKeyJwk,
      existing.publicKeyJwk
    );
    return {
      userId,
      deviceId: existing.deviceId,
      privateKey,
      publicKey,
      publicKeySpkiBase64: existing.publicKeySpkiBase64,
      label: existing.label,
    };
  }

  // Generate new keypair (extractable so we can stash JWK).
  const pair = (await crypto.subtle.generateKey(ECDH_ALGO, true, [
    "deriveKey",
    "deriveBits",
  ])) as CryptoKeyPair;

  const [privateKeyJwk, publicKeyJwk, publicKeySpkiBuffer] = await Promise.all([
    crypto.subtle.exportKey("jwk", pair.privateKey),
    crypto.subtle.exportKey("jwk", pair.publicKey),
    crypto.subtle.exportKey("spki", pair.publicKey),
  ]);
  const publicKeySpkiBase64 = bytesToBase64(publicKeySpkiBuffer);
  const deviceId = generateUuid();
  const label = guessDeviceLabel();

  await db.put("device_keypair", {
    userId,
    deviceId,
    privateKeyJwk,
    publicKeyJwk,
    publicKeySpkiBase64,
    label,
    createdAt: Date.now(),
  });

  return {
    userId,
    deviceId,
    privateKey: pair.privateKey,
    publicKey: pair.publicKey,
    publicKeySpkiBase64,
    label,
  };
};

/**
 * Import another device's public key from base64 SPKI (as stored in Convex).
 * Used to derive a shared secret with that device for family-key wrapping.
 */
export const importPublicKeySpki = async (spkiBase64: string): Promise<CryptoKey> => {
  const binary = atob(spkiBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return crypto.subtle.importKey("spki", bytes.buffer, ECDH_ALGO, true, []);
};

/**
 * Forget the device keypair (e.g. on full account reset).
 * Note: typical logout does NOT call this — keypair stays so re-login works.
 */
export const forgetDeviceKeypair = async (userId: string): Promise<void> => {
  const db = await getDB();
  await db.delete("device_keypair", userId);
};
